import "./index.scss";

import { Autocomplete, Box, MenuItem, Stack, TextField, TextFieldProps } from "@mui/material";
import { ReactComponent as ArrowDownIcon } from "assets/images/square-buttons/chevron-down-green.svg";
import Button from "components/Button";
import CheckBox from "components/Checkbox";
import DatePicker from "components/DatePicker";
import Dropdown from "components/Dropdown";
import Input from "components/Input";
import LabelWithValue from "components/LabelWithValue";
import PhoneInput from "components/PhoneInput";
import { TSearchableItem } from "components/SearchableList";
import TitleWithOptions from "components/TitleWithOptions";
import {
  COUNTRIES,
  COUNTRY_ISO3_CODES,
  DATE_FORMAT,
  DATE_FORMAT_STANDARD,
  GENERIC_MEMBER_STATUS_OPTIONS,
  MYSQL_DATE_FORMAT,
  USA_STATES,
} from "constants/index";
import { isOver100YearsOld, validatePhoneNumber } from "helpers/utils";
import _ from "lodash";
import {
  getLkpRandomFitlinxxId,
  verifyFitlinxxIdAvailability,
} from "modules/lookup/lookupServices";
import moment from "moment";
import { phone } from "phone";
import { useContext, useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

import { validateEmail } from "../../../../../modules/api/emailValidation";
import { MemberFormContext } from "../..";

const PrimaryDetailsTab = (props: {
  instructorOptions: TSearchableItem[];
  companyOptions: TSearchableItem[];
}) => {
  const { instructorOptions, companyOptions } = props;
  const memberFormContext = useContext(MemberFormContext);
  const formContext = useFormContext();
  const {
    control,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = formContext;
  const [currentStatus, setCurrentStatus] = useState(getValues("status"));
  const [isExpirationDateEnabled, setIsExpirationDateEnabled] = useState(false);
  const [isCancellationFieldsVisible, setIsCancellationFieldsVisible] = useState(false);
  const [loadingRandomFitlinxId, setLoadingRandomFitlinxId] = useState(false);
  const [loadingFitlinxIdVerification, setLoadingFitlinxIdVerification] = useState(false);
  const watchCountry = watch("country");

  useEffect(() => {
    if (memberFormContext.isEditMode) {
      if (["TERMINATED_FACILITY", "TERMINATED"].includes(currentStatus)) {
        setIsCancellationFieldsVisible(true);
        if (!getValues("cancellation_date")) {
          setValue("cancellation_date", moment().format(MYSQL_DATE_FORMAT));
        }
        if (!getValues("cancellation_reason")) {
          setValue("cancellation_reason", "");
        }
      } else {
        setIsCancellationFieldsVisible(false);
        setValue("cancellation_date", "");
        setValue("cancellation_reason", "");
      }
    }
  }, [currentStatus]);

  useEffect(() => {
    if (watchCountry !== "USA") {
      setValue("state", "");
    }
  }, [watchCountry]);

  const memberStatusOptions = () => {
    return GENERIC_MEMBER_STATUS_OPTIONS.map(
      (e, index) =>
        !e.isSelectAll && (
          <MenuItem
            key={`statusddlentry_${index}`}
            // selected={e.value === status}
            value={e.value}
          >
            {e.label}
          </MenuItem>
        )
    );
  };

  //#region event handlers
  const handleAutoSelectId = () => {
    setLoadingRandomFitlinxId(true);
    getLkpRandomFitlinxxId().then((res) => {
      setLoadingRandomFitlinxId(false);
      if (res.success) {
        setValue("fitlinxx_id", res.data);
      }
    });
  };

  const handleCheckId = () => {
    const fitlinxxId = getValues("fitlinxx_id");
    if (fitlinxxId) {
      setLoadingFitlinxIdVerification(true);
      verifyFitlinxxIdAvailability({ fitlinxx_id: fitlinxxId })
        .then((res) => {
          if (res.success) {
            toast(`The chosen FitLinxx ID is available.`, { type: "success" });
          }
        })
        .catch((e) => {
          toast(e[0], { type: "error" });
          setValue("fitlinxx_id", "");
        })
        .finally(() => setLoadingFitlinxIdVerification(false));
    }
  };
  //#endregion event handlers

  const handleCheckEmailIsValid = async (value): Promise<boolean> => {
    const email = value.toLowerCase();
    return await validateEmail(email);
  };

  const formatZipCode = (e) => {
    const postCode = e.target.value;
    if (postCode?.length && postCode?.length < 5 && watchCountry === "USA") {
      setValue("postal_code", `${"0".repeat(5 - postCode?.length)}${postCode}`);
    }
  };

  return (
    <div>
      {/* PERSONAL INFO */}
      <TitleWithOptions title="Personal Info" />
      <Stack direction="row" gap={4} my={4}>
        <Stack flex={1} gap={2} alignContent="left">
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              First name<span>*</span>
            </Box>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input
                    value={value}
                    onChange={onChange}
                    hasError={!_.isEmpty(errors.first_name)}
                    maxLength={20}
                  />
                  {errors.first_name?.type === "required" && (
                    <h4 className="error">This field is required</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Last name<span>*</span>
            </Box>
            <Controller
              name="last_name"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input
                    value={value}
                    onChange={onChange}
                    hasError={!_.isEmpty(errors.last_name)}
                    maxLength={30}
                  />
                  {errors.last_name?.type === "required" && (
                    <h4 className="error">This field is required</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Date of Birth<span>*</span>
            </Box>
            <Controller
              name="birth_date"
              control={control}
              rules={{
                required: true,
                validate: (value: Date) => {
                  if (
                    getValues("birth_date") === "Invalid date" ||
                    isOver100YearsOld(getValues("birth_date"))
                  ) {
                    return false;
                  } else {
                    return true;
                  }
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <DatePicker
                    className="fieldLabel"
                    value={new Date(moment(value, MYSQL_DATE_FORMAT).format(DATE_FORMAT_STANDARD))}
                    onChange={(v) => {
                      v.setHours(0);
                      setValue("birth_date", moment(v).format(MYSQL_DATE_FORMAT));
                      //setValue("birth_date", v);
                    }}
                    maxDate={new Date(moment().subtract(10, "year").format(DATE_FORMAT))}
                    minDate={new Date(moment().subtract(130, "year").format(DATE_FORMAT))}
                  />
                  {errors.birth_date?.type === "required" && (
                    <h4 className="error">This field is required</h4>
                  )}
                  {errors.birth_date?.type === "validate" && (
                    <h4 className="error">Please enter a valid date</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Gender<span>*</span>
            </Box>
            <Controller
              name="sex"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Dropdown
                    fullWidth
                    value={value}
                    onChange={(e) => setValue("sex", e.target.value)}
                  >
                    <MenuItem value="">Gender</MenuItem>
                    <MenuItem value="M">Male</MenuItem>
                    <MenuItem value="F">Female</MenuItem>
                  </Dropdown>
                  {errors.sex?.type === "required" && (
                    <h4 className="error">This field is required</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Weight
            </Box>
            <Controller
              name="weight"
              control={control}
              rules={{
                max: 9999,
                pattern: /^[1-9][0-9]*$|^0$/, // Integers only
                required: false,
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input
                    value={Number(value).toString()}
                    onChange={onChange}
                    type="number"
                    min="1"
                    hasError={!_.isEmpty(errors.weight)}
                  />
                  {errors.weight?.type === "max" && (
                    <h4 className="error">Weight cannot be more than 9999</h4>
                  )}
                  {errors.weight?.type === "pattern" && (
                    <h4 className="error">Weight cannot contain decimals</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Email address<span>*</span>
            </Box>
            <Controller
              name="email"
              control={control}
              rules={{
                pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                required: true,
                validate: (value) => handleCheckEmailIsValid(value),
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input
                    value={value}
                    type="email"
                    onChange={onChange}
                    hasError={!_.isEmpty(errors.email)}
                    maxLength={50}
                  />
                  {errors.email?.type === "required" && (
                    <h4 className="error">This field is required</h4>
                  )}
                  {errors.email?.type === "pattern" && <h4 className="error">Invalid email</h4>}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Primary phone
            </Box>
            <Controller
              name="phone"
              control={control}
              rules={{
                maxLength: 15,
                required: false,
                validate: (value: string) => {
                  return validatePhoneNumber(value, watchCountry);
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <div className="base-input">
                    <PhoneInput
                      iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                      international={true}
                      withCountryCallingCode={true}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.phone)}
                    />
                  </div>

                  {errors.phone?.type === "validate" && (
                    <>
                      <h4 className="error">Please add a valid phone number.</h4>
                      <p className="phone-hint">{COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}</p>
                    </>
                  )}
                  {errors.phone?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 15.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Other phone
            </Box>
            <Controller
              name="other_phone"
              control={control}
              rules={{
                maxLength: 20,
                required: false,
                validate: (value: string) => {
                  return validatePhoneNumber(value, watchCountry);
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <div className="base-input">
                    <PhoneInput
                      iso2={COUNTRY_ISO3_CODES[watchCountry]?.iso2}
                      international={true}
                      withCountryCallingCode={true}
                      value={value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.other_phone)}
                    />
                  </div>

                  {errors.other_phone?.type === "validate" && (
                    <>
                      <h4 className="error">Please add a valid phone number.</h4>
                      <p className="phone-hint">{COUNTRY_ISO3_CODES[watchCountry]?.hint || ""}</p>
                    </>
                  )}
                  {errors.other_phone?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 20.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
          {memberFormContext.isEditMode && (
            <Stack direction={"row"} alignContent="center" gap="40px">
              <Box className="fieldLabel" pt={"14px"}>
                Nickname
              </Box>
              <Controller
                name="nick_name"
                control={control}
                rules={{ required: false }}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1} flexGrow={1}>
                    <Input value={value} onChange={onChange} maxLength={20} />
                  </Stack>
                )}
              />
            </Stack>
          )}
          <Stack direction={"row"} flex={1} alignContent="left" mt="16px" gap="40px">
            <LabelWithValue label="Online Username" value={getValues("online_username")} />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Company Name
            </Box>
            <Controller
              name="company_name"
              control={control}
              rules={{ maxLength: 30, required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Autocomplete
                    freeSolo
                    value={value}
                    onChange={(_e, newValue) => setValue("company_name", newValue?.id)}
                    className="autocomplete-container"
                    popupIcon={<ArrowDownIcon />}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        onBlur={(e) => setValue("company_name", e.target.value)}
                        className={!_.isEmpty(errors.company_name) && `has-error`}
                      />
                    )}
                    options={companyOptions}
                  />
                  {errors.company_name?.type === "maxLength" && (
                    <>
                      <h4 className="error">Length can not be greater than 30.</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
        </Stack>
        <Stack flex={1} gap={2}>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Address
            </Box>
            <Controller
              name="address"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={50} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}></Box>
            <Controller
              name="address_2"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={50} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              City
            </Box>
            <Controller
              name="city"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={30} />
                </Stack>
              )}
            />
          </Stack>
          {watchCountry === "USA" && (
            <Stack direction={"row"} alignContent="center" gap="40px">
              <Box className="fieldLabel" pt={"14px"}>
                State
              </Box>
              <Controller
                name="state"
                control={control}
                rules={{ required: false }}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1} flexGrow={1}>
                    <Dropdown
                      fullWidth
                      value={value}
                      onChange={(e) => setValue("state", e.target.value)}
                    >
                      <MenuItem value="">State</MenuItem>
                      {USA_STATES.map((m, index) => (
                        <MenuItem value={m} key={`state_${index}`}>
                          {m}
                        </MenuItem>
                      ))}
                    </Dropdown>
                  </Stack>
                )}
              />
            </Stack>
          )}
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Zip Code
            </Box>
            <Controller
              name="postal_code"
              control={control}
              rules={{
                required: false,
                validate: (value: string) => {
                  if (value && watchCountry === "USA") {
                    return value.length === 5;
                  } else {
                    return true;
                  }
                },
              }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} onBlur={formatZipCode} maxLength={9} />
                  {errors.postal_code?.type === "validate" && (
                    <>
                      <h4 className="error">Length must be 5</h4>
                    </>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Country
            </Box>
            <Controller
              name="country"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Dropdown
                    fullWidth
                    value={value}
                    onChange={(e) => setValue("country", e.target.value)}
                  >
                    <MenuItem value="">Country</MenuItem>
                    {COUNTRIES.map((m, index) => (
                      <MenuItem value={m} key={`country_${index}`}>
                        {m}
                      </MenuItem>
                    ))}
                  </Dropdown>
                </Stack>
              )}
            />
          </Stack>
        </Stack>
      </Stack>
      {/* MEMBERSHIP */}
      <TitleWithOptions title="Membership" />
      <Stack direction="row" gap={4} mt={4}>
        <Stack flex={1} gap={2}>
          <h3>FitLinxx Information</h3>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              FitLinxx ID<span>*</span>
            </Box>
            <Stack direction={"row"} flexGrow={1} gap={`10px`}>
              <Controller
                name="fitlinxx_id"
                control={control}
                rules={{
                  required: true,
                  validate: (value: string) => {
                    if (value && value.length > 5) {
                      return false;
                    } else {
                      return true;
                    }
                  },
                }}
                render={({ field: { onChange, value, ref } }) => (
                  <Stack spacing={1} flexGrow={1}>
                    <Input
                      value={value.length > 5 ? "------" : value}
                      onChange={onChange}
                      hasError={!_.isEmpty(errors.fitlinxx_id)}
                      maxLength={5}
                      minLength={5}
                      extraComp={
                        <Button
                          label={"Auto Select ID"}
                          className={"text-button autoSelectIdButton buttonDisabledLoadingCursor"}
                          onClick={handleAutoSelectId}
                          type="button"
                          disabled={loadingRandomFitlinxId}
                        />
                      }
                    />
                    {errors.fitlinxx_id?.type === "required" && (
                      <h4 className="error">This field is required</h4>
                    )}
                    {errors.fitlinxx_id?.type === "validate" && (
                      <h4 className="error">Please choose a new FitLinxx ID</h4>
                    )}
                  </Stack>
                )}
              />
              <Button
                label={"Check ID"}
                color={"primary-blue"}
                onClick={handleCheckId}
                type="button"
                className="buttonDisabledLoadingCursor"
                disabled={loadingFitlinxIdVerification}
              />
            </Stack>
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Other Member ID
            </Box>
            <Controller
              name="other_member_id"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Input value={value} onChange={onChange} maxLength={25} />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Home Facility
            </Box>
            <Controller
              name="home_facility"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => <Input value={value} disabled />}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Sign Up Date
            </Box>
            <Controller
              name="sign_up_date"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <DatePicker
                    className="base-dropdown"
                    fullWidth={true}
                    value={new Date(value)}
                    onChange={(v) => {
                      setValue("sign_up_date", moment(v).format(MYSQL_DATE_FORMAT));
                    }}
                    disabled
                  />
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}></Box>
            <CheckBox
              checked={isExpirationDateEnabled}
              onChange={(v) => {
                setIsExpirationDateEnabled(v);
                if (!v) setValue("expiration_date", "");
              }}
              label={`Enable Expiration Date`}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Expiration Date
            </Box>
            <Controller
              name="expiration_date"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <DatePicker
                    className="base-dropdown"
                    fullWidth={true}
                    value={value}
                    minDate={new Date()}
                    onChange={(v) => onChange(v)}
                    disabled={!isExpirationDateEnabled}
                  />
                </Stack>
              )}
            />
          </Stack>
          {isCancellationFieldsVisible && (
            <>
              <Stack direction={"row"} alignContent="center" gap="40px">
                <Box className="fieldLabel" pt={"14px"}>
                  Cancellation Date
                </Box>
                <Controller
                  name="cancellation_date"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Stack spacing={1} flexGrow={1}>
                      <DatePicker
                        className="base-dropdown"
                        fullWidth={true}
                        value={new Date(moment(value).format(DATE_FORMAT))}
                        onChange={(v) => onChange(moment(v).format(MYSQL_DATE_FORMAT))}
                        minDate={new Date()}
                      />
                    </Stack>
                  )}
                />
              </Stack>
              <Stack direction={"row"} alignContent="center" gap="40px">
                <Box className="fieldLabel" pt={"14px"}>
                  Cancellation Reason
                </Box>
                <Controller
                  name="cancellation_reason"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, value, ref } }) => (
                    <Stack spacing={1} flexGrow={1}>
                      <Dropdown
                        fullWidth
                        value={value}
                        onChange={(e) => setValue("cancellation_reason", e.target.value)}
                      >
                        <MenuItem value="">Select</MenuItem>
                        <MenuItem value="AFFORDABILITY">Couldn't Afford the Expense</MenuItem>
                        <MenuItem value="LOCATION">Inconvenient Location</MenuItem>
                        <MenuItem value="INTEREST">Lost Interest/Motivation</MenuItem>
                        <MenuItem value="RELOCATION">Moved/Relocated</MenuItem>
                        <MenuItem value="TIME">Not Enough Time</MenuItem>
                        <MenuItem value="OTHER">Other</MenuItem>
                        <MenuItem value="FACILITY">Overcrowded Facility</MenuItem>
                        <MenuItem value="SWITCH_EXERCISES">Switched to Outdoor Exercise</MenuItem>
                      </Dropdown>
                    </Stack>
                  )}
                />
              </Stack>
            </>
          )}
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Status<span>*</span>
            </Box>
            <Controller
              name="status"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Dropdown
                    fullWidth
                    value={value}
                    onChange={(e) => {
                      setValue("status", e.target.value);
                      setCurrentStatus(e.target.value);
                    }}
                  >
                    {memberStatusOptions()}
                  </Dropdown>
                  {errors.status?.type === "required" && (
                    <h4 className="error">This field is required</h4>
                  )}
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Instructor
            </Box>
            <Controller
              name="trainer_id"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Dropdown
                    fullWidth
                    value={value}
                    onChange={(e) => setValue("trainer_id", e.target.value)}
                  >
                    {instructorOptions &&
                      instructorOptions.map((e, index) => (
                        <MenuItem key={`instructorddl_${index}`} value={e.value}>
                          {e.label}
                        </MenuItem>
                      ))}
                  </Dropdown>
                </Stack>
              )}
            />
          </Stack>
        </Stack>
        <Stack flex={1} gap={2}>
          <h3>System Options</h3>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}>
              Training Partner Sounds
            </Box>
            <Controller
              name="training_partner_sounds"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1} flexGrow={1}>
                  <Dropdown
                    fullWidth
                    value={value}
                    onChange={(e) => setValue("training_partner_sounds", e.target.value)}
                  >
                    <MenuItem value="0">All Sounds Off</MenuItem>
                    <MenuItem value="1">All Sounds On</MenuItem>
                    <MenuItem value="2">Warning Sounds Off</MenuItem>
                  </Dropdown>
                </Stack>
              )}
            />
          </Stack>
          <Stack direction={"row"} alignContent="center" gap="40px">
            <Box className="fieldLabel" pt={"14px"}></Box>
            <Controller
              name="change_weights"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1}>
                  <CheckBox checked={value} onChange={onChange} label={`Change Weights`} />
                </Stack>
              )}
            />
            <Controller
              name="change_settings"
              control={control}
              rules={{ required: false }}
              render={({ field: { onChange, value, ref } }) => (
                <Stack spacing={1}>
                  <CheckBox checked={value} onChange={onChange} label={`Change Settings`} />
                </Stack>
              )}
            />
          </Stack>
        </Stack>
      </Stack>
    </div>
  );
};

export default PrimaryDetailsTab;
